import fetch from 'cross-fetch';
import { ApolloClient, createHttpLink, InMemoryCache, from } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';

import { GRAPHQL_ENDPOINT } from 'utils/constants';
import PlatformLinkBuilder from '../utils/integration/PlatformLinkBuilder';

// Relay cursor connections paginated via `fetchMore({ variables: { after } })`
// (Tasks page: see src/pages/tasks/queries.ts + components/PaginationLinks.tsx).
// Without a field policy, each `Load More` page is written under a distinct cache
// field key (default keyArgs = all args, incl. `after`/`before`/`first`) and is never
// merged into the watched query, so the list never grows past the first page.
// `relayStylePagination` supplies both the keyArgs and an edges-concatenating merge.
// Connections scoped to a publication key on `publicationId` so each publication keeps
// its own list; the mention feeds take no `publicationId` arg.
const taskConnectionPolicies = (() => {
  const byPublication = [
    'currentTasks',
    'upcomingTasks',
    'readyToPublish',
    'overduePublicationSteps',
    'overdueStoriesSteps',
    'onHoldStoriesSteps',
    'unassignedSteps',
    'unassignedDueDateSteps',
    'unassignedPublishDateSteps',
    'storyActivities',
    'pitchesYouveSubmitted',
    'pitchesToReview',
    'activePitchRequests'
  ];
  const global = ['storyMentions', 'pitchMentions'];

  const fields: Record<string, ReturnType<typeof relayStylePagination>> = {};
  byPublication.forEach(name => {
    fields[name] = relayStylePagination(['publicationId']);
  });
  global.forEach(name => {
    fields[name] = relayStylePagination([]);
  });
  return fields;
})();

export function createApolloCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      Query: {
        fields: taskConnectionPolicies
      }
    }
  });
}

// Lifts the operation name into the URL so RUM, access logs and APM can tell calls apart.
export function withOperationName(base: string, operationName?: string | null): string {
  if (!operationName) return base;

  return `${base}${base.includes('?') ? '&' : '?'}op=${encodeURIComponent(operationName)}`;
}

export function useApollo(token: string | null) {
  const httpLink = createHttpLink({
    // Server URL (must be absolute)
    uri: (operation) =>
      withOperationName(PlatformLinkBuilder.build(`${GRAPHQL_ENDPOINT}`), operation.operationName),
    fetch,
    fetchOptions: { credentials: 'include' },
  });

  const client = new ApolloClient({
    cache: createApolloCache(),
    link: from([httpLink]),
    credentials: 'include'
  });

  return client;
}
